import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Bell, AlertTriangle } from 'lucide-react';
import { employeeApi } from '../lib/api.js';
import { useCurrentUser } from '../hooks/useCurrentUser.jsx';

// Mounted once at the app shell. When the current user is resolved,
// fetch any contract/document expiry notifications addressed to them
// (the expiry-reminder pipeline writes one row per recipient when an
// item enters its reminder window) and pop a sonner toast for each.
//
// We dismiss the row server-side once the toast goes up — re-displaying
// the same one every page load would be noisy. The toast persists with
// an action button so the user can jump straight to the relevant page.
//
// This component renders nothing.
export default function ExpiryNotificationsListener() {
  const { user } = useCurrentUser();
  const ranForUserRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    // Re-arm if the active user changes (e.g. logout → login as
    // someone else without a full reload).
    if (ranForUserRef.current === user.rowKey) return;
    ranForUserRef.current = user.rowKey;

    let cancelled = false;
    (async () => {
      try {
        const res = await employeeApi.listExpiryNotifications();
        const notifications = res?.notifications || [];
        if (cancelled || notifications.length === 0) return;

        const dismissKeys = [];
        for (const n of notifications) {
          dismissKeys.push(n.rowKey);
          const isContract = n.kind === 'contract';
          toast(n.title || (isContract ? 'Contract expiring' : 'Document expiring'), {
            description: n.body || '',
            duration: 12000,
            icon: isContract
              ? <AlertTriangle className="h-4 w-4 text-warning" />
              : <Bell className="h-4 w-4 text-primary" />,
            action: n.actionUrl
              ? {
                  label: 'View',
                  onClick: () => { window.location.href = n.actionUrl; },
                }
              : undefined,
          });
        }

        if (dismissKeys.length > 0) {
          // Clear them so refreshing the page doesn't re-pop the same
          // toasts. The cron will write fresh rows tomorrow if the
          // items still haven't been resolved.
          try {
            await employeeApi.dismissExpiryNotifications(dismissKeys);
          } catch {
            // best effort — toasts already shown
          }
        }
      } catch (err) {
        // 403/404 are expected when the user has no record or the
        // table doesn't exist yet (fresh tenant). Stay silent.
        if (err?.status && err.status >= 500) {
          // eslint-disable-next-line no-console
          console.warn('ExpiryNotificationsListener: failed to load', err);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  return null;
}
